import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight, Mail } from "lucide-react";
import { PHONE_NUMBER, PHONE_HREF } from "./EmergencyBanner";
import { allGeoTowns } from "@/data/geoPages";

const serviceLinks = [
  { label: "Well Pump Repair", href: "/services/well-pump-repair" },
  { label: "Well Pump Installation", href: "/services/well-pump-installation" },
  { label: "Grinder Pump Sales & Service", href: "/services/grinder-pump-repair" },
  { label: "Sewage Pump Systems", href: "/services/sewage-pump-systems" },
  { label: "Hydro-Fracking", href: "/services/hydro-fracking" },
  { label: "Water Filtration", href: "/services/water-filtration" },
  { label: "Emergency Service", href: "/services/emergency-pump-service" },
];

const quickLinks = [
  { label: "Service Area", href: "/service-area" },
  { label: "Reviews", href: "/reviews" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
  { label: "Resources", href: "/resources" },
];

export const Footer = () => (
  <footer className="bg-primary text-primary-foreground">
    {/* CTA strip */}
    <div className="border-b border-primary-foreground/10">
      <div className="container py-12 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="text-xl font-extrabold font-display mb-1 text-primary-foreground">Ready to Get Your Water Flowing?</h3>
          <p className="text-primary-foreground/60 text-sm">Call now or request a free quote — we respond fast.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-primary-foreground text-primary font-semibold px-6 py-3 rounded-lg text-sm hover:bg-primary-foreground/90 transition-colors"
          >
            Free Quote
          </Link>
          <a
            href={PHONE_HREF}
            className="inline-flex items-center gap-2 gradient-accent text-accent-foreground font-semibold px-6 py-3 rounded-lg text-sm hover:opacity-90 transition-opacity shadow-glow"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </div>
      </div>
    </div>

    <div className="container py-14 lg:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
      {/* Brand */}
      <div className="lg:pr-4">
        <p className="text-2xl font-extrabold font-display tracking-tight mb-1">SEMPER FI</p>
        <p className="text-sm font-medium text-primary-foreground/80 mb-4">Well & Pump Services</p>
        <p className="text-primary-foreground/80 text-sm text-pretty leading-relaxed mb-4">
          Veteran-owned well pump, grinder pump, and water system experts serving the Pocono Mountains with 33 years of experience.
        </p>
        <p className="text-primary-foreground/75 text-xs uppercase tracking-wider font-semibold">
          Factory Authorized Franklin Electric Distributor & Warranty Center
        </p>
      </div>

      {/* Services */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-primary-foreground/75 mb-5">Services</h4>
        <ul className="space-y-2.5">
          {serviceLinks.map((link) => (
            <li key={link.href}>
              <Link to={link.href} className="text-sm text-primary-foreground/60 hover:text-accent inline-flex items-center gap-1 group transition-colors">
                <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Quick Links */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-primary-foreground/75 mb-5">Company</h4>
        <ul className="space-y-2.5">
          {quickLinks.map((link) => (
            <li key={link.href}>
              <Link to={link.href} className="text-sm text-primary-foreground/60 hover:text-accent inline-flex items-center gap-1 group transition-colors">
                <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Contact */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-widest text-primary-foreground/75 mb-5">Contact</h4>
        <ul className="space-y-4 text-sm text-primary-foreground/60">
          <li>
            <a href={PHONE_HREF} className="flex items-center gap-3 hover:text-accent font-semibold text-primary-foreground/80 transition-colors">
              <Phone className="w-4 h-4 text-accent" /> {PHONE_NUMBER}
            </a>
          </li>
          <li>
            <a href="/contact" className="flex items-center gap-3 hover:text-accent transition-colors">
              <Mail className="w-4 h-4 shrink-0 text-primary-foreground/75" /> Contact Form
            </a>
          </li>
          <li className="flex items-center gap-3">
            <MapPin className="w-4 h-4 shrink-0 text-primary-foreground/75" /> 119 Lacey Ln, Saylorsburg, PA 18353
          </li>
        </ul>
      </div>
    </div>

    {/* Service Areas SEO links */}
    <div className="border-t border-primary-foreground/8">
      <div className="container py-8">
        <h4 className="text-xs font-bold uppercase tracking-widest text-primary-foreground/75 mb-4">Service Areas</h4>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {allGeoTowns.map(town => (
            <Link key={town.slug} to={town.href} className="text-xs text-primary-foreground/75 hover:text-accent transition-colors">
              {town.name}
            </Link>
          ))}
        </div>
      </div>
    </div>

    <div className="border-t border-primary-foreground/8">
      <div className="container py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-primary-foreground/70">
        <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span>© {new Date().getFullYear()} Semper Fi Well & Pump Services. All rights reserved.</span>
          <span aria-hidden="true" className="hidden sm:inline">·</span>
          <Link to="/privacy-policy" className="hover:text-accent transition-colors underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
        </p>
        <p className="uppercase tracking-wider font-medium">Veteran-Owned · Pocono Mountains · Est. 1992</p>
      </div>
    </div>
  </footer>
);
